import { z } from 'zod';
import { emailSchema } from './auth.js';

/**
 * Stan linku liczony z danych, nie przechowywany.
 * Trzymanie go w kolumnie oznaczałoby, że wygaśnięcie wymaga zadania cron
 * albo że stan bywa nieaktualny.
 */
export const SHARE_LINK_STATUSES = ['active', 'expired', 'revoked', 'exhausted'] as const;
export type ShareLinkStatus = (typeof SHARE_LINK_STATUSES)[number];

export const SHARE_LINK_STATUS_LABELS_PL: Readonly<Record<ShareLinkStatus, string>> = {
  active: 'Aktywny',
  expired: 'Wygasł',
  revoked: 'Unieważniony',
  exhausted: 'Wykorzystany',
};

export const DEFAULT_LINK_DAYS = 7;
export const DEFAULT_LINK_MAX_USES = 1;

export const createShareLinkSchema = z.object({
  /** Np. nazwa firmy klienta — służy odnalezieniu wpisu w historii. */
  clientLabel: z.string().trim().min(1, 'Podaj etykietę klienta.').max(160),
  expiresInDays: z
    .number()
    .int()
    .min(1, 'Minimum 1 dzień.')
    .max(90, 'Maksimum 90 dni.')
    .default(DEFAULT_LINK_DAYS),
  /** Domyślnie jednorazowy. */
  maxUses: z.number().int().min(1).max(10).default(DEFAULT_LINK_MAX_USES),
  /**
   * Kanał awaryjny, gdy zaplanowane cofnięcie ustawienia nie powiedzie się
   * przez martwy refresh token. Semantyka odświeżania jest u Cloudflare
   * nieudokumentowana, więc ten przypadek kiedyś wystąpi.
   */
  notifyEmail: emailSchema.optional(),
});
export type CreateShareLinkInput = z.infer<typeof createShareLinkSchema>;

export const shareLinkSchema = z.object({
  /**
   * Pełny token wraca WYŁĄCZNIE w odpowiedzi na utworzenie linku.
   * Na listach jest `null` — pracownik ma go skopiować od razu, a nie
   * podglądać cudze linki w historii.
   */
  token: z.string().nullable(),
  url: z.string().nullable(),
  tokenPrefix: z.string(),
  presetId: z.string(),
  presetName: z.string(),
  clientLabel: z.string(),
  notifyEmail: z.string().nullable(),
  expiresAt: z.string(),
  maxUses: z.number(),
  uses: z.number(),
  revokedAt: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  status: z.enum(SHARE_LINK_STATUSES),
});
export type ShareLink = z.infer<typeof shareLinkSchema>;

/** Pozycja listy presetów w panelu pracownika. */
export const presetListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  clientHeadline: z.string(),
  itemCount: z.number(),
  customRuleCount: z.number(),
  hasTemporary: z.boolean(),
  linkCount: z.number(),
  createdBy: z.string(),
  updatedAt: z.string(),
});
export type PresetListItem = z.infer<typeof presetListItemSchema>;
